import React from 'react';
import {
  phase0AnalysisArtifacts,
  latestPhase0MeetingSummary,
  phase0MeetingMinutes,
} from '../content/phase0Meetings';

export default function Progress() {
  const meetingTableHeaderClass =
    'px-4 py-3 border-b border-gray-200 font-semibold text-gray-900';
  const meetingTableCellClass = 'px-4 py-3 leading-relaxed';

  return (
    <div className="bg-white py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
            Phase 0 : Research and Roadmap
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select one clearly identified community for the first pilot school,
            supported by a written, evidence-based justification explaining why this
            location is the right starting point.
          </p>
        </div>

        {/* Core Principles */}
        <section className="mb-16">
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Phase 0 Goals:
            </h2>
            <ol className="space-y-5 text-gray-700">
              <li className="flex gap-3">
                <span className="font-semibold text-gray-900">1.</span>
                <div>
                  <p className="font-semibold text-gray-900">
                    Hire a local leader for Hamro Foolbari
                  </p>
                  <p>
                    Recruit a trusted local leader to coordinate community
                    engagement, local operations, and on-the-ground execution.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-gray-900">2.</span>
                <div>
                  <p className="font-semibold text-gray-900">
                    Shortlist candidate communities
                  </p>
                  <p>
                    Use school data, basic demographics, and local insight to
                    identify 3–5 viable locations.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-gray-900">3.</span>
                <div>
                  <p className="font-semibold text-gray-900">
                    Evaluate locations consistently
                  </p>
                  <p>
                    Apply a common comparison framework to assess need,
                    feasibility, community readiness, and accessibility.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-gray-900">4.</span>
                <div>
                  <p className="font-semibold text-gray-900">
                    Document the final choice
                  </p>
                  <p>
                    Produce a concise written rationale explaining the selection
                    and trade-offs.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-gray-900">5.</span>
                <div>
                  <p className="font-semibold text-gray-900">
                    Release budget for next 2 years
                  </p>
                  <p>
                    Publish a clear, projected two-year budget to align planning,
                    resourcing, and accountability.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* Meeting Minutes */}
        <section className="mb-16">
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Meeting Minutes
            </h2>
            <p className="text-gray-700 mb-6">
              Official records from Phase 0 coordination and planning meetings.
            </p>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {latestPhase0MeetingSummary.title}
              </h3>
              <p className="text-gray-700 mb-4">
                Participants: {latestPhase0MeetingSummary.participants.join(', ')}
              </p>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full table-fixed border border-gray-200 text-sm text-left">
                  <caption className="sr-only">
                    Phase 0 meeting summary by initiative and key actions.
                  </caption>
                  <colgroup>
                    <col className="w-[24%]" />
                    <col className="w-[76%]" />
                  </colgroup>
                  <thead className="bg-gray-100">
                    <tr>
                      <th
                        scope="col"
                        className={meetingTableHeaderClass}
                      >
                        Initiative / Area
                      </th>
                      <th
                        scope="col"
                        className={meetingTableHeaderClass}
                      >
                        Tasks / Key Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white text-gray-700 align-top">
                    {latestPhase0MeetingSummary.rows.map((row, index) => (
                      <tr key={row.area}>
                        <td
                          className={`${meetingTableCellClass} font-medium text-gray-900 ${
                            index < latestPhase0MeetingSummary.rows.length - 1
                              ? 'border-b border-gray-200'
                              : ''
                          }`}
                        >
                          {row.area}
                        </td>
                        <td
                          className={`${meetingTableCellClass} ${
                            index < latestPhase0MeetingSummary.rows.length - 1
                              ? 'border-b border-gray-200'
                              : ''
                          }`}
                        >
                          {row.actions}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-8 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Past Meeting Minutes
              </h3>
              <div className="flex flex-wrap gap-3">
                {phase0MeetingMinutes.map((minute) => (
                  <a
                    key={minute.href}
                    href={`${import.meta.env.BASE_URL}${minute.href}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {minute.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Document Repository */}
        <section className="mb-16">
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Document Repository
            </h2>
            <p className="text-gray-700 mb-6">
              Analysis artifacts and reference documents used during Phase 0
              planning.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed border border-gray-200 text-sm text-left">
                <caption className="sr-only">
                  Phase 0 analysis document repository.
                </caption>
                <colgroup>
                  <col className="w-[34%]" />
                  <col className="w-[46%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead className="bg-gray-100">
                  <tr>
                    <th scope="col" className={meetingTableHeaderClass}>
                      Document
                    </th>
                    <th scope="col" className={meetingTableHeaderClass}>
                      Use in Analysis
                    </th>
                    <th scope="col" className={meetingTableHeaderClass}>
                      Access
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white text-gray-700 align-top">
                  {phase0AnalysisArtifacts.map((artifact, index) => (
                    <tr key={artifact.href}>
                      <td
                        className={`${meetingTableCellClass} font-medium text-gray-900 ${
                          index < phase0AnalysisArtifacts.length - 1
                            ? 'border-b border-gray-200'
                            : ''
                        }`}
                      >
                        {artifact.title}
                      </td>
                      <td
                        className={`${meetingTableCellClass} ${
                          index < phase0AnalysisArtifacts.length - 1
                            ? 'border-b border-gray-200'
                            : ''
                        }`}
                      >
                        {artifact.description}
                      </td>
                      <td
                        className={`${meetingTableCellClass} ${
                          index < phase0AnalysisArtifacts.length - 1
                            ? 'border-b border-gray-200'
                            : ''
                        }`}
                      >
                        <a
                          href={`${import.meta.env.BASE_URL}${artifact.href}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Open PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section>
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <p className="text-lg text-gray-700 mb-2">
              This phase is not about perfection.
            </p>
            <p className="text-gray-700">
              It is about trust, clarity, and collective momentum.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
